import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../api.config";
import { listingChecklistApi } from "./listingChecklist.api";
import { leadScoringApi } from "./leadScoring.api";
import { dashboardApi } from "./dashboard.api";
import { transactionsApi } from "./transactions.api";
import { leadAnalyticsApi } from "./leadAnalytics.api";
import { smartListsApi } from "./smartLists.api";

export const clientsApi = createApi({
  reducerPath: "clientsApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["Client", "ClientStats", "TeamClients", "ClientTags"],
  // Keep unused data cached for 60 seconds to prevent disappearing data
  keepUnusedDataFor: 60,
  // Refetch on mount or arg change to ensure fresh data
  refetchOnMountOrArgChange: 30,
  endpoints: (build) => ({
    // Send affiliate sign-up email
    sendAffiliateSignUpEmail: build.mutation({
      query: (data) => ({
        url: "/clients/send-affiliate-email",
        method: "POST",
        body: data,
      }),
    }),

    // Create a new client
    createClient: build.mutation({
      query: (client) => ({
        url: "/clients",
        method: "POST",
        body: client,
      }),
      invalidatesTags: ["Client", "ClientStats"],
      // Cross-API: a freshly added client (e.g. dashboard quick-add → "mark
      // as lead") needs to show up on the pages that live in other slices,
      // not just the Clients list. The dashboard "Total Clients" tile reads
      // from DashboardStats (dashboardApi) and the Hot Leads / leaderboard
      // views read from leadScoringApi — without these dispatches a quick-add
      // would feel broken because the new lead wouldn't appear there until a
      // manual reload.
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(dashboardApi.util.invalidateTags(["DashboardStats"]));
          dispatch(leadScoringApi.util.invalidateTags(["LeadScores", "HotLeads", "ScoreStats"]));
          // Lead Capture Analytics totals come from leadAnalyticsApi —
          // without this the "Total Leads" / "New This Week" cards stay
          // stale after a quick-add until the user reloads.
          dispatch(leadAnalyticsApi.util.invalidateTags(["LeadAnalytics", "Leads", "SourcePerformance"]));
        } catch { /* mutation already toasted */ }
      },
    }),

    // Get all clients
    getAllClients: build.query({
      query: ({ status, page = 1, limit = 10, search = "" } = {}) => ({
        url: "/clients",
        method: "GET",
        params: { status, page, limit, search },
      }),
      providesTags: (result) => 
        result?.data?.clients
          ? [
              ...result.data.clients.map(({ _id }) => ({ type: 'Client', id: _id })),
              { type: 'Client', id: 'LIST' },
            ]
          : [{ type: 'Client', id: 'LIST' }],
    }),

    // Get all clients including shared (for Pro users)
    getAllClientsWithShared: build.query({
      query: ({ status, page = 1, limit = 100, search = "", includeShared = "true" } = {}) => ({
        url: "/clients/with-shared",
        method: "GET",
        params: { status, page, limit, search, includeShared },
      }),
      providesTags: ["Client", "TeamClients"],
    }),

    // Get clients shared with a team
    getTeamClients: build.query({
      query: (teamId) => ({
        url: `/clients/team/${teamId}`,
        method: "GET",
      }),
      providesTags: (result, error, teamId) => [{ type: "TeamClients", id: teamId }],
    }),

    // Share client with team
    shareClientWithTeam: build.mutation({
      query: ({ clientId, teamId, permissions = "view" }) => ({
        url: `/clients/${clientId}/share`,
        method: "POST",
        body: { teamId, permissions },
      }),
      invalidatesTags: ["Client", "TeamClients"],
    }),

    // Unshare client from team
    unshareClientFromTeam: build.mutation({
      query: ({ clientId, teamId }) => ({
        url: `/clients/${clientId}/share/${teamId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Client", "TeamClients"],
    }),

    // Get client by ID
    getClientById: build.query({
      query: (id) => ({
        url: `/clients/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "Client", id }],
    }),

    // Get client timeline. The backend endpoint is "public" but requires a
    // signed sharing token (anti-IDOR): clients open a shared link that
    // carries ?token=..., and the agent viewing in-app mints one for
    // themselves via getPortalToken (they own the client).
    getClientTimeline: build.query({
      query: ({ id, token }) => ({
        url: `/clients/public/${id}/timeline${token ? `?token=${encodeURIComponent(token)}` : ""}`,
        method: "GET",
      }),
    }),

    // Get portal token for a client (generates a signed shareable link)
    getPortalToken: build.query({
      query: (id) => ({
        url: `/clients/${id}/portal-token`,
        method: "GET",
      }),
    }),

    // Update client
    updateClient: build.mutation({
      query: ({ id, ...client }) => ({
        url: `/clients/${id}`,
        method: "PUT",
        body: client,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Client", id },
        { type: "Client", id: 'LIST' },
        "ClientStats"
      ],
      // Cross-API sync. A client's status / commission data feeds several
      // OTHER pages that live in their own RTK Query slices, so the
      // Client-tag invalidation above doesn't touch them — they'd stay
      // stale until a manual reload. Mirror what deleteClient already does
      // and fan the invalidation out so a status change (lead → active
      // buyer/seller, etc.) propagates everywhere at once:
      //   - dashboardApi   → "Total Clients" / status tiles on the dashboard
      //   - leadScoringApi → Hot Leads + leaderboard (a client leaving "lead"
      //                      status should drop off / re-rank immediately)
      //   - listingChecklistApi → workflow "for client X" badges
      //   - transactionsApi → Commission Tracker's "(from client)" rows are
      //                      synthesized server-side from client commission
      //                      data, so /transactions must refetch too
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(dashboardApi.util.invalidateTags(["DashboardStats"]));
          dispatch(leadScoringApi.util.invalidateTags(["LeadScores", "HotLeads", "ScoreStats"]));
          dispatch(listingChecklistApi.util.invalidateTags(["ListingChecklists", "ChecklistStats"]));
          dispatch(transactionsApi.util.invalidateTags(["Transactions"]));
          // Editing status / source on a client (e.g. closing them out)
          // changes what Lead Capture's totals + sources + funnel show.
          dispatch(leadAnalyticsApi.util.invalidateTags(["LeadAnalytics", "Leads", "SourcePerformance"]));
        } catch { /* mutation already surfaced the error */ }
      },
    }),

    // Distinct tag vocabulary across the user's contacts — powers the
    // freeform-tag autocomplete so tags stay consistent.
    getAllClientTags: build.query({
      query: () => ({ url: "/clients/tags/all", method: "GET" }),
      transformResponse: (res) => res?.data || [],
      providesTags: ["ClientTags"],
    }),

    // Incrementally add/remove tags on a contact. Atomic on the server
    // (no read-modify-write from the client), so it's safe to fire from
    // the Call Today widget, the client profile, or anywhere else.
    updateClientTags: build.mutation({
      query: ({ id, add, remove }) => ({
        url: `/clients/${id}/tags`,
        method: "PATCH",
        body: { add, remove },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Client", id },
        { type: "Client", id: "LIST" },
        "ClientTags",
      ],
      // Call Today rows carry tags too, so refresh that smart list when
      // tags change (a new tag might matter to how the user triages).
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(smartListsApi.util.invalidateTags(["CallToday"]));
        } catch { /* mutation already surfaced the error */ }
      },
    }),

    // Update last contact
    updateLastContact: build.mutation({
      query: (id) => ({
        url: `/clients/${id}/contact`,
        method: "PATCH",
      }),
      invalidatesTags: (result, error, id) => [{ type: "Client", id }, { type: "Client", id: 'LIST' }],
    }),

    // Delete client
    deleteClient: build.mutation({
      // Accepts either a bare id, or { id, deleteLinkedListing } to also
      // remove the client's linked listing (the backend keeps properties by
      // default). Deleting a listing never deletes the client — that stays
      // one-way.
      query: (arg) => {
        const id = typeof arg === "object" ? arg.id : arg;
        const alsoDeleteListing = typeof arg === "object" && arg.deleteLinkedListing;
        return {
          url: `/clients/${id}${alsoDeleteListing ? "?deleteLinkedListing=true" : ""}`,
          method: "DELETE",
        };
      },
      invalidatesTags: [{ type: "Client", id: 'LIST' }, "ClientStats"],
      // Backend cascade nulls clientId/clientName on any ListingChecklist
      // that referenced this client and deletes the matching LeadScore.
      // Refetch both so the workflow page doesn't show a stale
      // "for client X" badge and the Hot Leads widget drops the gone-
      // away lead instead of rendering a populated null clientId.
      async onQueryStarted(_id, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(listingChecklistApi.util.invalidateTags(["ListingChecklists", "ChecklistStats"]));
          dispatch(leadScoringApi.util.invalidateTags(["LeadScores", "HotLeads", "ScoreStats"]));
          dispatch(leadAnalyticsApi.util.invalidateTags(["LeadAnalytics", "Leads", "SourcePerformance"]));
        } catch { /* user already saw the toast */ }
      },
    }),

    // Get client stats
    getClientStats: build.query({
      query: () => ({
        url: "/clients/stats/overview",
        method: "GET",
      }),
      providesTags: ["ClientStats"],
    }),
  }),
});

export const {
  useSendAffiliateSignUpEmailMutation,
  useCreateClientMutation,
  useGetAllClientsQuery,
  useGetAllClientsWithSharedQuery,
  useGetTeamClientsQuery,
  useShareClientWithTeamMutation,
  useUnshareClientFromTeamMutation,
  useGetClientByIdQuery,
  useGetClientTimelineQuery,
  useGetPortalTokenQuery,
  useLazyGetPortalTokenQuery,
  useUpdateClientMutation,
  useGetAllClientTagsQuery,
  useUpdateClientTagsMutation,
  useUpdateLastContactMutation,
  useDeleteClientMutation,
  useGetClientStatsQuery,
} = clientsApi;
